import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Plus, Search, MoreVertical, Eye, Edit, Trash2, Calendar, Filter, MapPin, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  bozza: 'bg-slate-100 text-slate-800',
  programmato: 'bg-blue-100 text-blue-800',
  in_corso: 'bg-green-100 text-green-800',
  concluso: 'bg-slate-100 text-slate-800',
  annullato: 'bg-red-100 text-red-800'
};

const statusLabels = {
  bozza: 'Bozza',
  programmato: 'Programmato',
  in_corso: 'In Corso',
  concluso: 'Concluso',
  annullato: 'Annullato'
};

export default function Eventi() {
  const navigate = useNavigate();
  const [eventi, setEventi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statoFilter, setStatoFilter] = useState('all');

  useEffect(() => {
    fetchEventi();
  }, [statoFilter]);

  const fetchEventi = async () => {
    try {
      let url = `${API_URL}/api/eventi`;
      const params = new URLSearchParams();
      if (statoFilter && statoFilter !== 'all') {
        params.append('stato', statoFilter);
      }
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setEventi(data);
      }
    } catch (error) {
      console.error('Error fetching eventi:', error);
      toast.error('Errore nel caricamento degli eventi');
    } finally {
      setLoading(false);
    }
  };

  const handleAnnulla = async (evento) => {
    if (!window.confirm(`Sei sicuro di voler annullare l'evento "${evento.titolo}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/eventi/${evento.evento_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Evento annullato con successo');
        fetchEventi();
      } else {
        toast.error('Errore durante l\'annullamento');
      }
    } catch (error) {
      toast.error('Errore durante l\'annullamento');
    }
  };

  const filteredEventi = eventi.filter(e => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      e.titolo?.toLowerCase().includes(searchLower) ||
      e.luogo?.toLowerCase().includes(searchLower)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('it-IT', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6" data-testid="eventi-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="page-header mb-0">
          <h1 className="page-title">Eventi</h1>
          <p className="page-subtitle">Gestisci gli eventi e le attività dell'associazione</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/eventi/calendario')} data-testid="calendar-view-btn">
            <Calendar className="w-4 h-4 mr-2" />
            Calendario
          </Button>
          <Button className="btn-primary" onClick={() => navigate('/eventi/nuovo')} data-testid="add-evento-btn">
            <Plus className="w-4 h-4 mr-2" />
            Crea Evento
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Cerca per titolo o luogo..."
                className="pl-10"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="search-input"
              />
            </div>
            <Select value={statoFilter} onValueChange={setStatoFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="filter-stato">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtra per stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="programmato">Programmati</SelectItem>
                <SelectItem value="in_corso">In Corso</SelectItem>
                <SelectItem value="bozza">Bozza</SelectItem>
                <SelectItem value="concluso">Conclusi</SelectItem>
                <SelectItem value="annullato">Annullati</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner w-8 h-8" />
            </div>
          ) : filteredEventi.length === 0 ? (
            <div className="empty-state py-16">
              <Calendar className="empty-state-icon" />
              <p className="empty-state-title">Nessun evento trovato</p>
              <p className="empty-state-text">
                {search || statoFilter !== 'all' 
                  ? 'Prova a modificare i filtri di ricerca'
                  : 'Inizia creando il primo evento'}
              </p>
              {!search && statoFilter === 'all' && (
                <Button className="btn-primary mt-4" onClick={() => navigate('/eventi/nuovo')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Crea Evento
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Evento</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead className="hidden md:table-cell">Orario</TableHead>
                    <TableHead className="hidden lg:table-cell">Luogo</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="w-16">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEventi.map((evento) => (
                    <TableRow key={evento.evento_id} data-testid={`evento-row-${evento.evento_id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">{evento.titolo}</p>
                          {evento.tipologia && (
                            <p className="text-sm text-slate-500">{evento.tipologia}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          <span>{formatDate(evento.data)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {evento.orario_inizio && (
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-slate-400" />
                            <span>
                              {evento.orario_inizio}
                              {evento.orario_fine && ` - ${evento.orario_fine}`}
                            </span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {evento.luogo && (
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-slate-400" />
                            <span className="truncate max-w-[200px]">{evento.luogo}</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[evento.stato] || 'bg-slate-100 text-slate-800'}>
                          {statusLabels[evento.stato] || evento.stato}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`action-menu-${evento.evento_id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/eventi/${evento.evento_id}`)}>
                              <Eye className="w-4 h-4 mr-2" />
                              Visualizza
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/eventi/${evento.evento_id}/modifica`)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Modifica
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/presenze?evento=${evento.evento_id}`)}>
                              <Calendar className="w-4 h-4 mr-2" />
                              Presenze
                            </DropdownMenuItem>
                            {evento.stato !== 'annullato' && evento.stato !== 'concluso' && (
                              <DropdownMenuItem 
                                onClick={() => handleAnnulla(evento)}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Annulla
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
